import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { RefreshCw, Save, Wifi, WifiOff } from "lucide-react";

/**
 * ManageLiveCampaign — inline panel on the campaign detail page that
 * lets the campaign owner (or admin) adjust the mutable fields of the
 * *live* PureSpectrum survey and view its live quotas.
 *
 * Mutable fields (safe, non-immutable per PS platform):
 *   - offer_price (raw CPI paid to respondents)
 *   - completes_required (target completes on PS)
 *   - expected_ir / expected_loi / field_time
 *   - survey_title
 *
 * PS platform quirks locked-in by the backend helpers:
 *   - Updating `completes_required` DELETES existing quotas on PS's
 *     side (documented behaviour). We refresh the quota list after
 *     any survey mutation so the UI reflects reality.
 *   - Quota criteria are IMMUTABLE via PATCH — only `required_count`
 *     can be updated. To change criteria, the client must delete and
 *     re-add the quota. We surface PS's error message verbatim.
 */
export default function ManageLiveCampaign({ campaignId, onExternalChange }) {
  const [survey, setSurvey] = useState(null);
  const [quotas, setQuotas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  // Draft form state (initialized from server, only PATCHed on Save).
  const [draft, setDraft] = useState({
    offer_price: "", completes_required: "",
    expected_ir: "", expected_loi: "", field_time: "",
    survey_title: "",
  });

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [s, q] = await Promise.all([
        api.get(`/campaigns/${campaignId}/ps-survey`),
        api.get(`/campaigns/${campaignId}/ps-survey/quotas`),
      ]);
      setSurvey(s.data);
      setQuotas(q.data.quotas || []);
      setDraft({
        offer_price: s.data.offer_price ?? "",
        completes_required: s.data.completes_required ?? "",
        expected_ir: s.data.expected_ir ?? "",
        expected_loi: s.data.expected_loi ?? "",
        field_time: s.data.field_time ?? "",
        survey_title: s.data.survey_title ?? "",
      });
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [campaignId]);

  const dirty = survey && (
    Number(draft.offer_price) !== Number(survey.offer_price) ||
    Number(draft.completes_required) !== Number(survey.completes_required) ||
    Number(draft.expected_ir) !== Number(survey.expected_ir) ||
    Number(draft.expected_loi) !== Number(survey.expected_loi) ||
    Number(draft.field_time) !== Number(survey.field_time) ||
    String(draft.survey_title || "") !== String(survey.survey_title || "")
  );

  const save = async () => {
    setBusy(true); setError(null); setInfo(null);
    try {
      // Only include fields the user actually changed so PS doesn't
      // regenerate quotas unnecessarily (see completes_required side-effect).
      const deltas = {};
      if (Number(draft.offer_price) !== Number(survey.offer_price))
        deltas.offer_price = Number(draft.offer_price);
      if (Number(draft.completes_required) !== Number(survey.completes_required))
        deltas.completes_required = Number(draft.completes_required);
      if (Number(draft.expected_ir) !== Number(survey.expected_ir))
        deltas.expected_ir = Number(draft.expected_ir);
      if (Number(draft.expected_loi) !== Number(survey.expected_loi))
        deltas.expected_loi = Number(draft.expected_loi);
      if (Number(draft.field_time) !== Number(survey.field_time))
        deltas.field_time = Number(draft.field_time);
      if (String(draft.survey_title || "") !== String(survey.survey_title || ""))
        deltas.survey_title = String(draft.survey_title || "");
      const { data } = await api.patch(`/campaigns/${campaignId}/ps-survey`, deltas);
      setInfo(`Updated ${Object.keys(data.updated || {}).join(", ")}.`);
      await load();
      onExternalChange?.();
    } catch (e) {
      setError(e.response?.data?.detail?.body || e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const updateQuotaCount = async (buyerId, newCount) => {
    setBusy(true); setError(null); setInfo(null);
    try {
      await api.patch(`/campaigns/${campaignId}/ps-survey/quotas/${buyerId}`, {
        required_count: Number(newCount),
      });
      setInfo(`Quota "${buyerId}" updated to ${newCount} completes.`);
      await load();
    } catch (e) {
      // Surface PS's exact error body verbatim (e.g. 1007 immutable criteria).
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.body || e.message));
    } finally {
      setBusy(false);
    }
  };

  const deleteQuota = async (buyerId) => {
    if (!confirm(`Delete quota "${buyerId}"? This cannot be undone from the UI.`)) return;
    setBusy(true); setError(null); setInfo(null);
    try {
      await api.delete(`/campaigns/${campaignId}/ps-survey/quotas/${buyerId}`);
      setInfo(`Quota "${buyerId}" deleted.`);
      await load();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === "string" ? d : (d?.body || e.message));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="border border-[var(--rl-border)] bg-white p-6" data-testid="manage-live-campaign-loading">
        <div className="text-sm text-[var(--rl-text-secondary)]">Loading panel status…</div>
      </div>
    );
  }

  if (error && !survey) {
    return (
      <div className="border border-[var(--rl-accent)] bg-white p-6" data-testid="manage-live-campaign-error">
        <div className="flex items-center gap-2 font-semibold text-[var(--rl-accent)]">
          <WifiOff className="w-4 h-4" /> Panel provider unreachable
        </div>
        <div className="text-sm mt-2 text-[var(--rl-text-secondary)]">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
        <button onClick={load} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold underline">
          <RefreshCw className="w-3 h-3" /> Retry
        </button>
      </div>
    );
  }

  return (
    <div className="border border-[var(--rl-border)] bg-white p-6" data-testid="manage-live-campaign">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-bold text-lg">Manage Live Campaign</h3>
          <div className="text-xs text-[var(--rl-text-secondary)] mt-1 flex items-center gap-1">
            <Wifi className="w-3 h-3" /> PS Survey #{survey.ps_survey_id} · status {survey.ps_survey_status}
          </div>
        </div>
        <button onClick={load} disabled={busy} className="text-xs font-mono underline text-[var(--rl-text-secondary)]" data-testid="manage-refresh-btn">
          <RefreshCw className={`w-3 h-3 inline ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-xs font-semibold text-[var(--rl-text-secondary)] uppercase tracking-wider">Survey title</span>
          <input
            className="mt-1 w-full border border-[var(--rl-border)] px-3 py-2 text-sm"
            value={draft.survey_title}
            onChange={(e) => setDraft({ ...draft, survey_title: e.target.value })}
            data-testid="manage-title-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-[var(--rl-text-secondary)] uppercase tracking-wider">Offer price (raw CPI, USD)</span>
          <input
            type="number" step="0.01" min="0.01"
            className="mt-1 w-full border border-[var(--rl-border)] px-3 py-2 text-sm font-mono"
            value={draft.offer_price}
            onChange={(e) => setDraft({ ...draft, offer_price: e.target.value })}
            data-testid="manage-offer-price-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-[var(--rl-text-secondary)] uppercase tracking-wider">Completes required</span>
          <input
            type="number" min="1"
            className="mt-1 w-full border border-[var(--rl-border)] px-3 py-2 text-sm font-mono"
            value={draft.completes_required}
            onChange={(e) => setDraft({ ...draft, completes_required: e.target.value })}
            data-testid="manage-completes-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-[var(--rl-text-secondary)] uppercase tracking-wider">Expected IR (%)</span>
          <input
            type="number" min="1" max="100"
            className="mt-1 w-full border border-[var(--rl-border)] px-3 py-2 text-sm font-mono"
            value={draft.expected_ir}
            onChange={(e) => setDraft({ ...draft, expected_ir: e.target.value })}
            data-testid="manage-ir-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-[var(--rl-text-secondary)] uppercase tracking-wider">Expected LOI (min)</span>
          <input
            type="number" min="1"
            className="mt-1 w-full border border-[var(--rl-border)] px-3 py-2 text-sm font-mono"
            value={draft.expected_loi}
            onChange={(e) => setDraft({ ...draft, expected_loi: e.target.value })}
            data-testid="manage-loi-input"
          />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-[var(--rl-text-secondary)] uppercase tracking-wider">Field time (days)</span>
          <input
            type="number" min="1"
            className="mt-1 w-full border border-[var(--rl-border)] px-3 py-2 text-sm font-mono"
            value={draft.field_time}
            onChange={(e) => setDraft({ ...draft, field_time: e.target.value })}
            data-testid="manage-field-time-input"
          />
        </label>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={save}
          disabled={!dirty || busy}
          className="inline-flex items-center gap-2 bg-[var(--rl-brand)] text-white px-4 py-2 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-90 transition"
          data-testid="manage-save-btn"
        >
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save changes"}
        </button>
        {dirty && (
          <span className="text-xs text-[var(--rl-text-secondary)] font-mono">
            Note: changing completes_required regenerates PS-side quotas.
          </span>
        )}
      </div>

      {info && <div className="mt-3 text-sm text-emerald-700" data-testid="manage-info">{info}</div>}
      {error && (
        <div className="mt-3 text-sm text-[var(--rl-accent)]" data-testid="manage-error">
          {typeof error === "string" ? error : JSON.stringify(error)}
        </div>
      )}

      {/* ===== Quotas ===== */}
      <div className="mt-6 border-t border-[var(--rl-border)] pt-4">
        <div className="text-sm font-bold mb-3">Live quotas ({quotas.length})</div>
        {quotas.length === 0 ? (
          <div className="text-xs text-[var(--rl-text-secondary)]">
            No quotas defined on this survey — PS is running an unrestricted total-completes goal.
          </div>
        ) : (
          <div className="space-y-2" data-testid="manage-quotas-list">
            {quotas.map((q) => (
              <QuotaRow
                key={q.ps_quota_id}
                quota={q}
                onUpdateCount={(n) => updateQuotaCount(q.buyer_quota_id, n)}
                onDelete={() => deleteQuota(q.buyer_quota_id)}
                busy={busy}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function QuotaRow({ quota, onUpdateCount, onDelete, busy }) {
  const [draft, setDraft] = useState(String(quota.required_count));
  useEffect(() => setDraft(String(quota.required_count)), [quota.required_count]);
  const changed = Number(draft) !== Number(quota.required_count);
  return (
    <div
      className="grid grid-cols-[1fr_100px_auto_auto] items-center gap-2 border border-[var(--rl-border)] px-3 py-2"
      data-testid={`manage-quota-${quota.buyer_quota_id}`}
    >
      <div>
        <div className="text-sm font-semibold">{quota.buyer_quota_id}</div>
        <div className="text-[10px] font-mono text-[var(--rl-text-secondary)]">
          {(quota.criteria || []).length} criterion · fielded {quota.completed_count || 0}
        </div>
      </div>
      <input
        type="number" min="1"
        className="border border-[var(--rl-border)] px-2 py-1 text-sm font-mono w-full"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
      />
      <button
        onClick={() => onUpdateCount(draft)}
        disabled={!changed || busy}
        className="text-xs font-semibold underline disabled:opacity-30 disabled:cursor-not-allowed"
        data-testid={`manage-quota-save-${quota.buyer_quota_id}`}
      >
        Save
      </button>
      <button
        onClick={onDelete}
        disabled={busy}
        className="text-xs font-semibold text-[var(--rl-accent)] underline disabled:opacity-30"
        data-testid={`manage-quota-delete-${quota.buyer_quota_id}`}
      >
        Delete
      </button>
    </div>
  );
}
